/**
 * Transloadit response codes, grouped by WHO can recover
 * (https://transloadit.com/docs/api/response-codes/), plus Transforge's own vocabulary — our
 * self-hosted half does NOT speak Transloadit's codes: its gateway returns INTERNAL_ERROR /
 * INVALID_REQUEST / SIGNATURE_AUTHENTICATION_FAILED / ASSEMBLY_ID_COLLISION, and a failed
 * assembly puts free text in `error` with the bounded code in `failed_steps[].error_code`
 * (only the webhook lane sees that; listed here so the two lanes stay parallel).
 *
 * Kept deliberately parallel to upload-processor `app/upload-failure.log.js`: the browser
 * sees the creation-time rejections (synchronous 4xx from `POST /assemblies`) that the
 * webhook never receives, and the webhook sees the post-creation ones. The same code must
 * classify the same on both lanes. Anything not listed stays `unclassified` until prod
 * data says otherwise — sets are widened from measurement, never guessed.
 */

/** Only the USER can fix it, by uploading a different file (or they abandoned the upload). */
export const USER_FILE_ASSEMBLY_ERRORS: ReadonlySet<string> = new Set([
  'INPUT_FILE_EMPTY',
  'FILE_FILTER_DECLINED_FILE', // Transforge / robot-level
  'MAX_SIZE_EXCEEDED',
  'INVALID_INPUT_ERROR',
  'INVALID_FILE_META_DATA',
  'FILE_META_DATA_ERROR',
  'POSSIBLY_MALICIOUS_FILE_FOUND',
  'USER_COMMAND_ERROR',
  'FILE_DOWNLOAD_ERROR',
  'IMPORT_FILE_ERROR',
  'ASSEMBLY_EXPIRED',
  'REQUEST_PREMATURE_CLOSED',
  'INPUT_FILE_MISSING', // Transforge step code
]);

/** The provider had a bad moment; the same input would likely succeed on a replay. */
export const PROVIDER_TRANSIENT_ASSEMBLY_ERRORS: ReadonlySet<string> = new Set([
  'ASSEMBLY_CRASHED',
  'WORKER_JOB_ERROR',
  'INTERNAL_COMMAND_ERROR',
  'INTERNAL_COMMAND_TIMEOUT',
  'ASSEMBLY_JOB_ENQUEUE_ERROR',
  'TMP_FILE_DOWNLOAD_ERROR',
  'NO_RPC_RESULT_FROM_IMAGE_RESIZER',
  'SYNC_ERROR',
  'GET_ACCOUNT_DB_ERROR',
  'TEMPLATE_DB_ERROR',
  'CANNOT_ACCEPT_NEW_ASSEMBLIES',
  'RATE_LIMIT_REACHED',
  'ASSEMBLY_STATUS_FETCHING_RATE_LIMIT_REACHED',
  'SERVER_500',
  'ASSEMBLY_NOTIFICATION_NOT_PERSISTED',
  'INTERNAL_ERROR', // Transforge gateway
  'TEMPLATE_SERVICE_UNAVAILABLE',
  'MISSING_DEPENDENCY',
]);

/**
 * A signature that EXPIRED is the user's timing, not a broken key: open-api signs a 1h
 * window and a dialog left open crosses it (measured: 5 events / 5 accounts / 4 days).
 * Transforge's validator returns SIGNATURE_AUTHENTICATION_FAILED for missing / expired /
 * wrong-key / wrong-signature alike, so it cannot be told apart from expiry. A real key
 * break is ~100% of uploads and shows on rate — never page-on-first for these.
 */
export const STALE_SIGNATURE_ERRORS: ReadonlySet<string> = new Set([
  'AUTH_EXPIRED',
  'SIGNATURE_AUTHENTICATION_FAILED',
]);

/** OUR signature, auth key, template, stored credentials, params or account — fails for everyone. */
export const CONFIG_ASSEMBLY_ERRORS: ReadonlySet<string> = new Set([
  // auth / signature (AUTH_EXPIRED / SIGNATURE_AUTHENTICATION_FAILED are STALE_SIGNATURE_ERRORS)
  'INVALID_SIGNATURE',
  'AUTHENTICATION_REQUIRED', // Transforge gateway
  'AUTHENTICATION_ERROR',
  'INSUFFICIENT_PERMISSIONS',
  'SIGNATURE_REUSE_DETECTED',
  'NO_SIGNATURE_FIELD',
  'GET_ACCOUNT_UNKNOWN_AUTH_KEY',
  'INSUFFICIENT_AUTH_SCOPE',
  'NO_AUTH_PARAMETER',
  'NO_AUTH_KEY_PARAMETER',
  'NO_AUTH_EXPIRES_PARAMETER',
  'NO_OBJECT_AUTH_PARAMETER',
  'INVALID_AUTH_EXPIRES_PARAMETER',
  'INVALID_AUTH_KEY_PARAMETER',
  'INVALID_AUTH_MAX_SIZE_PARAMETER',
  'INVALID_AUTH_REFERER_PARAMETER',
  'REFERER_MISMATCH', // per-tenant: a white-label domain missing from the key's referer list
  // template
  'NO_TEMPLATE_ID',
  'TEMPLATE_NOT_FOUND',
  'TEMPLATE_INVALID_JSON',
  'TEMPLATE_VALIDATION_ERROR',
  'TEMPLATE_DENIES_STEPS_OVERRIDE',
  'TEMPLATE_CREDENTIALS_NOT_FOUND',
  'TEMPLATE_CREDENTIALS_INJECTION_ERROR',
  'TEMPLATE_CREDENTIALS_NOT_READ',
  'INVALID_TEMPLATE_FIELD',
  // steps / robots
  'ASSEMBLY_NO_STEPS',
  'ASSEMBLY_EMPTY_STEPS',
  'ASSEMBLY_INVALID_STEPS',
  'ASSEMBLY_STEP_INVALID',
  'ASSEMBLY_STEP_INVALID_ROBOT',
  'ASSEMBLY_STEP_INVALID_USE',
  'ASSEMBLY_STEP_NO_ROBOT',
  'ASSEMBLY_STEP_UNKNOWN_ROBOT',
  'ASSEMBLY_STEP_UNKNOWN_USE',
  'ASSEMBLY_INFINITE',
  'ASSEMBLY_DISALLOWED_ROBOTS_USED',
  'ASSEMBLY_ROBOT_MISSING',
  'ASSEMBLY_NO_CHARGEABLE_STEP',
  'ASSEMBLY_URL_TRANSFORM_MISSING',
  'NO_RESULT_STEP_FOUND',
  'CDN_REQUIRED',
  'INVALID_STEP_NAME',
  'INVALID_UPLOAD_HANDLE_STEP_NAME',
  'ASSEMBLY_MEMORY_LIMIT_EXCEEDED',
  // request shape (ours: the Uppy integration / signed params)
  'ASSEMBLY_INVALID_NOTIFY_URL',
  'ASSEMBLY_INVALID_NUM_EXPECTED_UPLOAD_FILES_PARAM',
  'INVALID_PARAMS_FIELD',
  'NO_PARAMS_FIELD',
  'NO_OBJECT_PARAMS_FIELD',
  'INVALID_FORM_DATA',
  'DO_NOT_REUSE_ASSEMBLY_IDS',
  'ASSEMBLY_SATURATED',
  'ASSEMBLY_FILE_NOT_RESERVED',
  'INVALID_ASSEMBLY_STATUS',
  'SERVER_401',
  'SERVER_403',
  'SERVER_404',
  'INVALID_REQUEST', // Transforge gateway
  'ASSEMBLY_ID_COLLISION',
  'ASSEMBLY_NOT_FOUND',
  'NO_FILE_PROVIDED',
  'STEPS_VALIDATION_ERROR', // Transforge step codes
  'PARAMS_VALIDATION_ERROR',
  'MISSING_TEMPLATE_OR_STEPS',
  'MISSING_ROBOT',
  'MISSING_AUTH',
  // account / billing
  'ASSEMBLY_ACCOUNT_MISMATCH',
  'ADMIN_PERMISSIONS_REQUIRED',
  'VERIFIED_EMAIL_REQUIRED',
  'BILL_LIMIT_EXCEEDED',
  'PLAN_LIMIT_EXCEEDED',
  'BAD_PRICING',
  'INCOMPLETE_PRICING',
  'NO_PRICING',
  'NO_COUNTRY',
]);
